import React, { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { useToast } from '../../../../../components/common/Toast';
import { useAccessTheme } from '../useAccessTheme';
import { EntraCheckbox, EntraRadioOption, EntraSection, EntraToggleRow } from './EntraWizardUi';
import {
  entraSsoLoginUrl,
  getEntraSsoConfig,
  saveEntraSsoConfig,
  type EntraSsoConfig,
} from './entraConnectionService';
import type { EntraWizardState } from './entraWizardTypes';

interface EntraSsoStepProps {
  connectionId: string;
  onChange?: (partial: Partial<EntraWizardState>) => void;
}

const EntraSsoStep: React.FC<EntraSsoStepProps> = ({ connectionId, onChange }) => {
  const t = useAccessTheme();
  const { showToast } = useToast();
  const [config, setConfig] = useState<EntraSsoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getEntraSsoConfig(connectionId)
      .then((data) => {
        if (!active) return;
        setConfig(data);
        onChange?.({ ssoEnabled: data.isEnabled, userMatchMode: data.allowEmailLinking ? 'email' : 'approval' });
      })
      .catch((err: Error) => {
        if (active) showToast({ type: 'error', message: err.message });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [connectionId]);

  const save = async (next: EntraSsoConfig) => {
    setSaving(true);
    try {
      const saved = await saveEntraSsoConfig(connectionId, {
        isEnabled: next.isEnabled,
        enforcementMode: next.enforcementMode,
        allowEmailLinking: next.allowEmailLinking,
      });
      setConfig(saved);
      onChange?.({ ssoEnabled: saved.isEnabled });
      showToast({ type: 'success', message: 'SSO configuration saved.' });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Unable to save SSO configuration' });
    } finally {
      setSaving(false);
    }
  };

  const copyRedirect = async () => {
    if (!config?.redirectUri) return;
    try {
      await navigator.clipboard.writeText(config.redirectUri);
      showToast({ type: 'success', message: 'Redirect URI copied.' });
    } catch {
      showToast({ type: 'error', message: 'Unable to copy redirect URI' });
    }
  };

  const testSignIn = () => {
    const popup = window.open(entraSsoLoginUrl(connectionId, 'test'), 'entra-sso-test', 'width=520,height=720');
    const onMessage = (event: MessageEvent) => {
      if (!event.data || event.data.type !== 'ENTRA_SSO_TEST') return;
      window.removeEventListener('message', onMessage);
      if (event.data.ok) {
        showToast({ type: 'success', message: 'Microsoft sign-in test succeeded.' });
        onChange?.({ ssoTested: true });
      } else {
        showToast({ type: 'error', message: event.data.message || 'Microsoft sign-in test failed.' });
      }
    };
    window.addEventListener('message', onMessage);
    if (!popup) {
      window.removeEventListener('message', onMessage);
      showToast({ type: 'error', message: 'Allow popups to test Microsoft sign-in.' });
    }
  };

  if (loading || !config) {
    return <p className={`text-sm ${t.muted}`}>Loading SSO configuration...</p>;
  }

  return (
    <>
      <EntraSection title="Single Sign-On" description="Allow users to securely sign in using Microsoft Entra ID.">
        <EntraToggleRow
          label="Microsoft Entra SSO"
          checked={config.isEnabled}
          onChange={(isEnabled) => setConfig({ ...config, isEnabled })}
        />
      </EntraSection>

      <EntraSection title="Protocol">
        <p className={`text-sm ${t.title}`}>OpenID Connect (OIDC)</p>
      </EntraSection>

      <EntraSection title="Redirect URI" description="Register this Web redirect URI on the App Registration and enable ID tokens.">
        <div className="flex items-center gap-2">
          <code className={`flex-1 text-xs break-all rounded-lg border px-3 py-2 ${t.input}`}>{config.redirectUri}</code>
          <button type="button" onClick={copyRedirect} className={'p-2 rounded-lg shrink-0 ' + t.btnSecondary} title="Copy">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </EntraSection>

      <EntraSection title="Sign-In Policy">
        <div className="space-y-2">
          <EntraRadioOption
            name="enforcement"
            value="optional"
            checked={config.enforcementMode === 'optional'}
            title="Optional"
            description="Users can use existing Selfic login or Microsoft."
            onChange={(enforcementMode) => setConfig({ ...config, enforcementMode })}
          />
          <EntraRadioOption
            name="enforcement"
            value="requiredForDirectoryUsers"
            checked={config.enforcementMode === 'requiredForDirectoryUsers'}
            title="Required for Entra Users"
            description="Entra-linked users must use Microsoft."
            onChange={(enforcementMode) => setConfig({ ...config, enforcementMode })}
          />
          <EntraRadioOption
            name="enforcement"
            value="requiredForTenant"
            checked={config.enforcementMode === 'requiredForTenant'}
            title="Required for Tenant"
            description="Microsoft SSO is required for tenant users."
            onChange={(enforcementMode) => setConfig({ ...config, enforcementMode })}
          />
        </div>
      </EntraSection>

      <EntraSection title="User Matching">
        <EntraCheckbox
          label="Allow verified email matching for first-time linking"
          checked={config.allowEmailLinking}
          onChange={(allowEmailLinking) => setConfig({ ...config, allowEmailLinking })}
        />
      </EntraSection>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={testSignIn} className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}>
          Test Microsoft Sign-In
        </button>
        <button
          type="button"
          onClick={() => save(config)}
          disabled={saving}
          className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ' + t.btnPrimary}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>
    </>
  );
};

export default EntraSsoStep;
